import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Ban, Bug, Building2, FileStack, LockOpen, MessageCircleWarning, Scale } from "lucide-react";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { ClaimWithRelations } from "../api/use-claim";

export type BlockedTypeKey = "TECHNICAL" | "MISSING_DOC" | "LEGAL" | "COMMERCIAL" | "OTHER";

/** Port of utils/config/constants/blocked-types.ts (Vue). */
export const blockedTypes: Record<BlockedTypeKey, { displayText: string; Icon: typeof Bug }> = {
  TECHNICAL: { displayText: "Technique", Icon: Bug },
  MISSING_DOC: { displayText: "Pièce manquante", Icon: FileStack },
  LEGAL: { displayText: "Juridique", Icon: Scale },
  COMMERCIAL: { displayText: "Commercial", Icon: Building2 },
  OTHER: { displayText: "Autre", Icon: MessageCircleWarning },
};

export const blockedTypeOptions = (Object.keys(blockedTypes) as BlockedTypeKey[]).map((type) => ({
  type,
  ...blockedTypes[type],
}));

/** Mirrors BlockedFlagModal.resolve (Vue). */
export function useResolveBlockedFlag(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notify.promise(adminAPI.resolveBlockedFlag(claimId), { success: "Dossier débloqué" }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors BlockedFlagModal.create/changeType + BlockedFlagConversation.addComment (Vue). */
export function useUpsertBlockedFlag(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      successMessage,
    }: {
      payload: { type?: BlockedTypeKey; description: string };
      successMessage: string;
    }) => notify.promise(adminAPI.upsertBlockedFlag(claimId, payload), { success: successMessage }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Port of BlockedFlagBanner.vue. */
export function BlockedFlagBanner({ claim }: { claim: ClaimWithRelations }) {
  const flag = claim.activeBlockedFlag ?? null;

  const resolveMutation = useResolveBlockedFlag(claim.id);

  if (!flag) {
    return null;
  }

  const { displayText, Icon } = blockedTypes[flag.type];

  return (
    <div className="mt-4 mb-4 overflow-hidden rounded-lg border border-zinc-200 bg-white text-left shadow-sm">
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Ban className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-amber-800">Dossier bloqué</span>
              <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-600/20 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700">
                <Icon className="h-3 w-3" />
                {displayText}
              </span>
            </div>
            <div className="mt-1 text-[11.5px] text-[#a16207]">
              Depuis le{" "}
              <span className="tabular-nums">
                {format(new Date(flag.blockedSince), "d MMMM yyyy", { locale: fr })}
              </span>{" "}
              · par {flag.createdBy?.fullName}
            </div>
            <div className="mt-1.5 text-[13px] leading-normal whitespace-pre-wrap text-amber-900">
              {flag.description}
            </div>
          </div>
          <button
            type="button"
            disabled={resolveMutation.isPending}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-[13px] font-semibold whitespace-nowrap text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
            onClick={() => resolveMutation.mutate()}
          >
            <LockOpen className="h-3.5 w-3.5" />
            Débloquer
          </button>
        </div>
      </div>
    </div>
  );
}
